import {
  cancelMessagingOperation,
  getRewardReceiversById,
  getRewards,
  setUpdateReward,
} from 'admin/actions/rewards';
import CSVButton from 'admin/components/shared/CSVButton';
import Pager from 'admin/components/shared/Pager';
import { formatDate, formatJstFromUtc, formatUUID, rewardStatus, toQueryParams } from 'admin/utils';
import React, { useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import styled from 'styled-components';
import { openAlertModal } from '~/javascripts/admin/actions/alert-modal';
import { useAppDispatch } from '~/javascripts/admin/hooks/useApp';
import { createBadge } from '../../../atoms/Badge';
import { Button } from '../../../atoms/Buttons';
import Link from '../../../atoms/Link';
import Loader from '../../../atoms/Loader';
import Table from '../../../atoms/Table';

const LoaderWrapper = styled.div`
  width: 100%;
  height: 150px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const HEADERS = [
  { label: '状態', size: 1.5, align: 'left' },
  { label: 'ID', size: 1.5, align: 'left' },
  { label: '登録日時', size: 1, align: 'left' },
  { label: '最終更新日時', size: 1, align: 'left' },
  { label: '予約日時', size: 1, align: 'left' },
  { label: '作成者', size: 3, align: 'left' },
  { label: '付与人数', size: 1, align: 'right' },
  { label: '付与総額', size: 1, align: 'right' },
  { label: '', size: 2, align: 'right' },
] as const;

const RewardStatusBadge = createBadge({
  'reward-draft': { label: '下書き', bgColor: 'gray', color: 'white' },
  'reward-reserve': {
    label: '予約',
    bgColor: '#3C86C9',
    color: 'white',
  },
  'reward-done': {
    label: '実行済',
    bgColor: '#208637',
    color: 'white',
  },
});

type Pagination = { current: number; per_page: number };

type RewardRow = {
  id: string;
  done_at?: string | null;
  updated_at?: string;
  reserve_at?: string | null;
  created_by: { name: string };
  money_amount: number;
  point_amount: number;
  receiver_count?: number;
};

type RewardsResponse = { rows: RewardRow[]; pagination: Pagination };

type Props = {
  getRewards: (query?: string) => any;
  setUpdateReward: (reward: any) => void;
  selectedMoneyId?: string;
  setApiResult?: (result?: any) => void;
};

const CollectableRewards = ({
  getRewards,
  setUpdateReward,
  selectedMoneyId,
  setApiResult = () => {},
}: Props) => {
  const dispatch = useAppDispatch();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [rewards, setRewards] = useState<RewardsResponse>({
    rows: [],
    pagination: { current: 1, per_page: 50 },
  });

  useEffect(() => {
    fetchRewards();
  }, [selectedMoneyId]);

  const fetchRewards = useCallback(
    (page = 1) => {
      if (!selectedMoneyId) return;

      setIsLoading(true);

      const params = {
        page,
        per_page: rewards.pagination.per_page,
        private_money_id: selectedMoneyId,
      };
      getRewards(toQueryParams(params))
        .then(setRewards)
        .finally(() => setIsLoading(false));
    },
    [selectedMoneyId, rewards.pagination.per_page]
  );

  const fetchReward = (reward: RewardRow) =>
    getRewardReceiversById(reward.id).then((recievers: any[]) =>
      recievers.map((r) => ({
        ...r,
        registered_at: formatJstFromUtc(r.registered_at),
        last_send_message_at: formatJstFromUtc(r.last_send_message_at),
        money_amount: reward.money_amount,
        point_amount: reward.point_amount,
      }))
    );

  const csvFileName = (id: string) => `reward_${formatUUID(id)}.csv`;

  const openCancelReserveModal = (id: string) =>
    dispatch(
      openAlertModal({
        body: <span>本当に削除してよろしいですか？</span>,
        confirmText: '実行',
        confirmColor: 'danger',
        onConfirm: () => {
          cancelMessagingOperation(id)
            .then(() => fetchRewards(rewards.pagination.current))
            .catch((err) =>
              Promise.resolve(err).then((error) =>
                setApiResult([{ text: error.message, isError: true }])
              )
            )
            .finally(() => {
              setTimeout(() => setApiResult(undefined), 5000);
            });
        },
      })
    );

  return (
    <>
      {isLoading ? (
        <LoaderWrapper>
          <Loader type="Oval" width={50} height={50} />
        </LoaderWrapper>
      ) : (
        <Table styles={{ marginTop: '20px' }}>
          <Table.Header>
            <Table.Row>
              {HEADERS.map((h) => (
                <Table.Cell size={h.size} key={h.label} align={h.align}>
                  {h.label}
                </Table.Cell>
              ))}
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {rewards.rows.map((row) => {
              const status = rewardStatus(row);
              const totalAmount = (row.money_amount + row.point_amount) * (row.receiver_count ?? 0);

              return (
                <Table.Row>
                  <Table.Cell wordBreak="keep-all">
                    <RewardStatusBadge status={status.name ?? ''} />
                  </Table.Cell>
                  <Table.Cell wordBreak="keep-all">
                    {status.name === 'reward-draft' ? (
                      <Link to="/promotion/rewards/create" onClick={() => setUpdateReward(row)}>
                        {formatUUID(row.id)}
                      </Link>
                    ) : (
                      formatUUID(row.id)
                    )}
                  </Table.Cell>
                  <Table.Cell>{row.done_at ? formatDate(row.done_at) : '-'}</Table.Cell>
                  <Table.Cell>{formatDate(row.updated_at)}</Table.Cell>
                  <Table.Cell>
                    {row.reserve_at ? formatDate(row.reserve_at, 'YYYY/MM/DD HH:mm') : ''}
                  </Table.Cell>
                  <Table.Cell wordBreak="break-word">{row.created_by.name}</Table.Cell>
                  <Table.Cell align="right" wordBreak="break-word">
                    {row.receiver_count?.toLocaleString('ja-JP')}
                  </Table.Cell>
                  <Table.Cell align="right" wordBreak="break-word">
                    {totalAmount.toLocaleString('ja-JP')}
                  </Table.Cell>
                  <Table.Cell align="right">
                    {status.name === 'reward-reserve' ? (
                      <Button
                        variant="muted-outline"
                        onClick={() => openCancelReserveModal(row.id)}
                      >
                        予約キャンセル
                      </Button>
                    ) : (
                      <CSVButton
                        fetchData={() => fetchReward(row)}
                        fileName={csvFileName(row.id)}
                      />
                    )}
                  </Table.Cell>
                </Table.Row>
              );
            })}
          </Table.Body>
          <Table.Footer>
            <Table.Row noBorder>
              <Table.Cell colSpan={9} align="center" styles={{ paddingTop: '16px' }}>
                <Pager pagination={rewards.pagination} onChangePage={fetchRewards} />
              </Table.Cell>
            </Table.Row>
          </Table.Footer>
        </Table>
      )}
    </>
  );
};

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      getRewards,
      setUpdateReward,
    },
    dispatch
  );

export default connect(null, mapDispatchToProps)(CollectableRewards);
